import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, Table, Modal, Select, Spin, Card, message, Upload } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const categories = [
  { label: 'چوب جادوگری', value: 'wand' },
  { label: 'کتاب‌ها', value: 'books' },
  { label: 'لباس', value: 'robes' },
  { label: 'دواهای جادویی', value: 'potion' },
  { label: 'سایر', value: 'other' }
];

const years = [1, 2, 3, 4, 5, 6, 7];

// نمایش قیمت فقط بر اساس پول دنیای جادوگری (مثل کیف‌پول/بانک) — هیچ‌جا تومان نشان داده نمی‌شود
function formatGalleonPrice(record) {
  const g = record?.priceGalleons || 0;
  const s = record?.priceSickles || 0;
  const k = record?.priceKnuts || 0;
  const parts = [];
  if (g > 0) parts.push(`${g}G`);
  if (s > 0) parts.push(`${s}S`);
  if (k > 0 || parts.length === 0) parts.push(`${k}K`);
  return parts.join(' ');
}

export default function RequirementManager() {
  const [form] = Form.useForm();
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedYear, setSelectedYear] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [file, setFile] = useState(null);
  // واحد قیمت‌گذاری در فرم: 'toman' → تبدیل خودکار به گالیون در سرور، یا 'galleon' → مستقیم
  const [priceUnit, setPriceUnit] = useState('toman');

  useEffect(() => {
    loadRequirements(1);
  }, []);

  const loadRequirements = async (year) => {
    setLoading(true);
    try {
      const response = await adminAPI.getRequirements(year);
      if (response.data.success) {
        setRequirements(response.data.requirements);
        setSelectedYear(year);
      }
    } catch (error) {
      message.error('خطا در بارگذاری وسایل');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description);
      formData.append('priceUnit', priceUnit);
      if (priceUnit === 'toman') {
        formData.append('priceTomans', values.priceTomans || 0);
      } else {
        formData.append('priceGalleons', values.priceGalleons || 0);
        formData.append('priceSickles', values.priceSickles || 0);
        formData.append('priceKnuts', values.priceKnuts || 0);
      }
      formData.append('schoolYear', selectedYear);
      formData.append('category', values.category);
      formData.append('isMandatory', values.isMandatory || false);
      formData.append('order', values.order);
      if (file) formData.append('image', file);

      let response;
      if (editingId) {
        response = await adminAPI.updateRequirement(editingId, formData);
      } else {
        response = await adminAPI.createRequirement(formData);
      }

      if (response.data.success) {
        message.success('وسیله با موفقیت ذخیره شد!');
        form.resetFields();
        setFile(null);
        setEditingId(null);
        setIsModalVisible(false);
        loadRequirements(selectedYear);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'خطا در ذخیره وسیله');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      try {
        await adminAPI.deleteRequirement(id);
        message.success('وسیله حذف شد');
        loadRequirements(selectedYear);
      } catch (error) {
        message.error('خطا در حذف');
      }
    }
  };

  const handleEdit = (record) => {
    // اگر قبلاً با قیمت تومانی ثبت شده (record.price > 0)، همان واحد را برای ویرایش پیش‌فرض می‌کنیم
    const unit = record.price > 0 ? 'toman' : 'galleon';
    setPriceUnit(unit);
    form.setFieldsValue({ ...record, priceTomans: record.price });
    setEditingId(record._id);
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'نام',
      dataIndex: 'name'
    },
    {
      title: 'توضیح',
      dataIndex: 'description',
      ellipsis: true
    },
    {
      title: 'قیمت (پول دنیای جادویی)',
      render: (_, record) => formatGalleonPrice(record)
    },
    {
      title: 'دسته',
      dataIndex: 'category'
    },
    {
      title: 'اجباری',
      render: (_, record) => record.isMandatory ? '✅' : '❌'
    },
    {
      title: 'ترتیب',
      dataIndex: 'order'
    },
    {
      title: 'عمل',
      render: (_, record) => (
        <span>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            ویرایش
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          >
            حذف
          </Button>
        </span>
      )
    }
  ];

  return (
    <Card title="مدیریت وسایل مدرسه">
      <div style={{ marginBottom: '16px' }}>
        <Select
          value={selectedYear}
          onChange={loadRequirements}
          style={{ width: '200px' }}
        >
          {years.map((year) => (
            <Select.Option key={year} value={year}>
              سال {year}
            </Select.Option>
          ))}
        </Select>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setEditingId(null);
            setFile(null);
            setPriceUnit('toman');
            setIsModalVisible(true);
          }}
          style={{ marginLeft: '10px' }}
        >
          وسیله جدید
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={requirements}
          rowKey="_id"
          pagination={false}
        />
      </Spin>

      <Modal
        title={editingId ? 'ویرایش وسیله' : 'وسیله جدید'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item
            name="name"
            label="نام"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="توضیح"
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item label="واحد قیمت‌گذاری">
            <Select
              value={priceUnit}
              onChange={setPriceUnit}
              options={[
                { label: 'تومان (تبدیل خودکار با نرخ فعلی صرافی گرینگاتس)', value: 'toman' },
                { label: 'پول دنیای جادویی (گالیون / سیکل / نات)', value: 'galleon' }
              ]}
            />
          </Form.Item>
          <p style={{ color: '#888', fontSize: 12, marginTop: -12, marginBottom: 16 }}>
            توجه: به هر کاربر، در همه‌جای سایت، فقط قیمت به پول دنیای جادوگری نمایش داده می‌شود.
            اگر تومان وارد کنید، فقط برای مرجع داخلی ذخیره می‌شود و در همان لحظه به گالیون/سیکل/نات تبدیل می‌گردد.
          </p>

          {priceUnit === 'toman' ? (
            <Form.Item
              name="priceTomans"
              label="قیمت (تومان)"
              rules={[{ required: true, message: 'قیمت تومانی را وارد کنید' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          ) : (
            <>
              <Form.Item name="priceGalleons" label="گالیون" rules={[{ required: true, message: 'حداقل مقدار گالیون را وارد کنید' }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
              <Form.Item name="priceSickles" label="سیکل">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
              <Form.Item name="priceKnuts" label="نات">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </>
          )}

          <Form.Item
            name="category"
            label="دسته"
            rules={[{ required: true }]}
          >
            <Select options={categories} />
          </Form.Item>

          <Form.Item
            name="order"
            label="ترتیب"
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isMandatory"
            label="اجباری"
            valuePropName="checked"
          >
            <input type="checkbox" />
          </Form.Item>

          <Form.Item label="تصویر">
            <Upload
              maxCount={1}
              onChange={({ fileList }) => {
                if (fileList.length > 0) {
                  setFile(fileList[0].originFileObj);
                }
              }}
            >
              <Button>انتخاب تصویر</Button>
            </Upload>
          </Form.Item>

          <Button type="primary" htmlType="submit" block loading={loading}>
            ذخیره
          </Button>
        </Form>
      </Modal>
    </Card>
  );
}
